import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Clock } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

export default function TrainingPortal() {
  const { user, apiClient } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCourses = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/courses');
      setCourses(data);
    } catch { toast.error('Failed to load courses'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleEnroll = async (courseId) => {
    if (!user) { toast.error('Please login to enroll'); return; }
    try {
      const originUrl = window.location.origin;
      const { data } = await apiClient.post('/api/payments/create-checkout', null, {
        params: { origin_url: originUrl, item_type: 'course', item_id: courseId },
      });
      window.location.href = data.url;
    } catch { toast.error('Failed to initiate payment.'); }
  };

  return (
    <div className="min-h-screen pt-16 bg-[var(--bg-alt)]" data-testid="training-portal-page">
      {/* Hero */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-[var(--primary-blue)]" />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, var(--primary-green) 0%, transparent 40%)',
        }} />
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-[var(--primary-green)] mb-4">Professional Development</p>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">Clinician Training Portal</h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
              Advance your skills with comprehensive training in autism diagnosis, neuropsych evaluation, and insurance billing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Courses */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-blue)]" />
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-16 card-elevated rounded-2xl">
              <p className="text-[var(--text-body)] text-lg">No courses available. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course, i) => (
                <motion.div key={course.id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                  className="card-elevated overflow-hidden" data-testid={`course-${course.id}`}>
                  <img
                    src="https://images.unsplash.com/photo-1741769766414-188500c6d143?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-lg font-heading font-bold mb-2">{course.title}</h3>
                    <p className="text-sm text-[var(--text-body)] mb-4">{course.description}</p>

                    <div className="mb-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-[var(--primary-blue)] mb-2">Curriculum</p>
                      <ul className="text-xs text-[var(--text-body)] space-y-1">
                        {course.curriculum.slice(0, 3).map((item, idx) => (
                          <li key={`${course.id}-${idx}`} className="flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-[var(--primary-green)]" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-[var(--border-color)]">
                      <div>
                        <p className="text-2xl font-heading font-bold text-[var(--primary-blue)]">${course.price}</p>
                        <div className="flex items-center gap-1 text-xs text-[var(--text-body)]">
                          <Users size={12} /> {course.enrolled_count} enrolled
                        </div>
                      </div>
                      <button onClick={() => handleEnroll(course.id)} className="btn-primary text-sm !px-5 !py-2" data-testid={`enroll-${course.id}-btn`}>
                        Enroll Now
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Custom Training */}
          <div className="mt-16 card-elevated p-8 text-center">
            <h3 className="text-2xl font-heading font-bold mb-3">Need Custom Training?</h3>
            <p className="text-[var(--text-body)] mb-6">We offer tailored programs for organizations.</p>
            <Link to="/contact" className="btn-outline inline-flex items-center gap-2 text-sm">
              Contact Us <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
